import { Position } from 'geojson';
import { FuelUnit } from '../enums';
import { FlightImpactDto } from '../impacts/flight-impact';

export enum RouteType {
    flown = 'flown',
    planned = 'planned',
    routeAi = 'routeAi',
    atc = 'atc',
    impact = 'impact'
}

export enum PlannedRouteType {
    planned,
    atc
}

export enum AirportFunction {
    adequate,
    suitable
}

export enum EtopsPointType {
    etopsEntry = 0,
    etopsExit = 1,
    etp = 2,
    coverageEntry = 3,
    coverageExit = 4,
    terrainAvoidance = 5,
    equalFuel = 6
}

export interface EtopsAirport {
    position: Position;
    iataId: string;
    icaoId: string;
    aptFunc: AirportFunction;
}

export interface EtopsPoint {
    name: string | null;
    type: EtopsPointType;
    position: Position;
    criticalTime: number | null;
    fob: number | null;
    fobUnit: number | null;
    behindNdx: number | null;
    aheadNdx: number | null;
}
export interface EtopsData {
    borderDistance: number | null;
    borderTime: number | null;
    ruleDistance: number | null;
    ruleTime: number | null;
    airports: EtopsAirport[];
    points: EtopsPoint[];
}

export enum WaypointType {
    NULL = 0,
    DepartureAirport = 1,
    ArrivalAirport = 2,
    PrimaryArrivalAlternateAirport = 3,
    ArrivalAlternateAirport = 4,
    Enroute = 5,
    FreeFlightEnroute = 6,
    GridPoint = 7,
    Ndb = 8,
    Vhf = 9,
    Compulsory = 10,
    AtsItem15 = 11,
    AirspaceBoundary = 12,
    OceanicAirspaceBoundary = 13,
    ETOPSEntry = 14, // Matches ETOPS EEP point type
    ETOPSExit = 15, // Matches ETOPS EXP point type
    EqualTimePoint = 16, // Matches ETOPS ETP point type
    EqualFuelPoint = 17, // Matches ETOPS EFP point type
    PointOfNoReturn = 18,
    ContingencySavingsDecisionWaypoint = 19,
    TopOfClimb = 20,
    TopOfDescent = 21,
    TopOfStepClimb = 22,
    TopOfStepDescent = 23,
    BottomOfClimb = 24,
    BottomOfDescent = 25,
    BottomOfStepClimb = 26,
    BottomOfStepDescent = 27,
    OTS = 28,
    DriftDownDecisionPoint = 29,
    DepressurisationDecisionPoint = 30,
    ReportingPoint = 31,
    ContingencySavingAirport = 32,
    ContingencySavingEnRouteAlternateAirport = 33,
    PrimaryContingencySavingAlternate = 34,
    ContingencySavingAlternate = 35,
    CoverageEntry = 36, // Matches ETOPS EN point type
    CoverageExit = 37 // Matches ETOPS EX point type
}

export function isEtopsWaypoint(pointType: WaypointType | null | undefined) {
    return (
        !!pointType &&
        (pointType === WaypointType.ETOPSEntry ||
            pointType === WaypointType.ETOPSExit ||
            pointType === WaypointType.EqualTimePoint ||
            pointType === WaypointType.EqualFuelPoint ||
            pointType === WaypointType.CoverageEntry ||
            pointType === WaypointType.CoverageExit)
    );
}

export function etopsPointTypeToWaypoint(etopsPointType: EtopsPointType): WaypointType {
    switch (etopsPointType) {
        case EtopsPointType.etopsEntry:
            return WaypointType.ETOPSEntry;
        case EtopsPointType.etp:
            return WaypointType.EqualTimePoint;
        case EtopsPointType.etopsExit:
            return WaypointType.ETOPSExit;
        case EtopsPointType.equalFuel:
            return WaypointType.EqualFuelPoint;
        case EtopsPointType.coverageEntry:
            return WaypointType.CoverageEntry;
        case EtopsPointType.coverageExit:
            return WaypointType.CoverageExit;
        default:
            // Terrain avoidance not covered
            return WaypointType.NULL;
    }
}

export interface RouteWaypointModel {
    position: [number, number];
    name: string;
    altitude: number | null;
    fob: number | null;
    type: WaypointType | null;
}

export interface FlightForwardRoutePoint {
    position: [number, number];
    altitude: number | null;
}

export interface FlightForwardRouteModel {
    points: FlightForwardRoutePoint[];
}

export interface FlightPlanRouteModel {
    id: number;
    timestamp: number | null;
    text: string;
    waypoints: RouteWaypointModel[];
    etops: EtopsData | null;
    cruisingAltitude: number | null;
    fuelUnit: FuelUnit;
    impacts: FlightImpactDto[];
}

export interface RouteFlownPointModel {
    position: [number, number];
    altitude: number | null;
    time: number | null;
    speed: number | null;
    fob: number | null;
    fobUnit: FuelUnit | null;
    feedId: number | null;
}

export type FlightFlownRouteModel = RouteFlownPointModel[];

export interface FlightRoutes {
    atc: FlightPlanRouteModel | null;
    planned: FlightPlanRouteModel | null;
    flown: RouteFlownPointModel[] | null;
    destination: {
        name: string;
        position: [number, number];
    } | null;
}

export interface FlightForwardPointModel {
    position: [number, number];
    altitude: number | null;
    time: number | null;
    heading: number | null;
}

export interface RouteWindsData {
    altitudes: number[];
    elements: RouteWindItem[];
}

export interface RouteWindItem {
    dar: number;
    oar: number;
    alts: RouteWindItemAltitude[];
}

export interface RouteWindItemAltitude {
    u: number;
    v: number;
    t: number;
}
